import { useState, useRef, useEffect } from 'react';
import { Upload, X, Loader2, Image as ImageIcon, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ImageUpload = ({ 
  onUploadSuccess, 
  currentImage = null,
  folder = 'products',
  className = '' 
}) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentImage);
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  // Update preview when currentImage prop changes
  useEffect(() => {
    setPreview(currentImage);
  }, [currentImage]);

  const handleFileSelect = async (file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select a valid image file (JPG, PNG, WebP)');
      return;
    }

    // Validate file size (max 2MB as per user requirement)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 2MB');
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload to server
    setUploading(true);
    setUploadProgress(0);
    try {
      const token = localStorage.getItem('admin_token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', folder);

      const response = await axios.post(`${API}/admin/upload-image`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        }
      });

      if (response.data.success) {
        setPreview(response.data.url);
        onUploadSuccess(response.data.url);
        toast.success('Image uploaded successfully!');
      }
    } catch (error) {
      console.error('Upload error:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to upload image. Please try again.';
      toast.error(errorMsg);
      setPreview(currentImage); // Revert to original
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const removeImage = () => {
    setPreview(null);
    onUploadSuccess('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={`${className}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleChange}
        className="hidden"
        data-testid="file-input"
      />

      {preview ? (
        // Image Preview
        <div className="relative group" data-testid="image-preview">
          <img
            src={preview}
            alt="Preview"
            className="w-full h-48 object-cover rounded-lg border border-white/10"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?auto=format&fit=crop&w=800&q=80';
            }}
          />
          {uploading && (
            <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center rounded-lg">
              <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin mb-2" />
              <div className="w-3/4 bg-white/20 rounded-full h-2">
                <div 
                  className="bg-[#D4AF37] h-2 rounded-full transition-all duration-300" 
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-xs text-white mt-2">{uploadProgress}%</p>
            </div>
          )}
          {!uploading && (
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 rounded-lg">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
                title="Replace image"
              >
                <Upload className="w-5 h-5 text-white" />
              </button>
              <button
                type="button"
                onClick={removeImage}
                className="p-2 bg-red-500/80 hover:bg-red-500 rounded-full transition-colors"
                title="Remove image"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          )}
          {!uploading && preview.startsWith('http') && (
            <div className="absolute top-2 right-2 bg-green-500 text-white p-1 rounded-full">
              <Check className="w-4 h-4" />
            </div>
          )}
        </div>
      ) : (
        // Upload Area
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`
            w-full h-48 border-2 border-dashed rounded-lg cursor-pointer
            flex flex-col items-center justify-center gap-3 transition-colors
            ${dragActive 
              ? 'border-[#D4AF37] bg-[#D4AF37]/10' 
              : 'border-white/20 hover:border-[#D4AF37]/50 bg-[#1A1A1A]'
            }
            ${uploading ? 'pointer-events-none' : ''}
          `}
          data-testid="upload-area"
        >
          {uploading ? (
            <>
              <Loader2 className="w-10 h-10 text-[#D4AF37] animate-spin" />
              <p className="text-sm text-[#A1A1AA]">Uploading...</p>
            </>
          ) : (
            <>
              <div className="w-14 h-14 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                <ImageIcon className="w-7 h-7 text-[#D4AF37]" />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-white">
                  Tap to upload or drag & drop
                </p>
                <p className="text-xs text-[#A1A1AA] mt-1">
                  JPG, PNG, WebP (max 2MB)
                </p>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
